// ============================================================================
// lib/exercise-v2/unlock-poll.ts — attente de l'activation d'accès post-achat.
// ============================================================================
// Après un achat IN-APP natif (RevenueCat / StoreKit), la feuille système se
// ferme AVANT que le webhook RC n'ait écrit l'accès en base (`subscriptions` /
// `access_rights`) — typiquement 1 à 5 s de latence. Contrairement au web
// (retour Stripe = rechargement complet de page avec `?pass_success=1`), le
// natif ne navigue pas : il faut donc SONDER l'accès jusqu'à ce qu'il bascule,
// puis reprendre l'exercice sans que l'élève ait à quitter/rouvrir la page.
//
// Fonction PURE (testable) : elle ne connaît ni Supabase ni React — on lui
// passe un `checkAllowed` qui renvoie l'état d'accès courant. Le sleep est
// injectable pour des tests déterministes.
// ============================================================================

export interface UnlockPollOptions {
  /** Renvoie `true` dès que l'accès est débloqué côté serveur. */
  checkAllowed: () => Promise<boolean>;
  /** Nombre maximum de sondages (défaut 20 → ~30 s à 1,5 s d'intervalle). */
  maxAttempts?: number;
  /** Délai entre deux sondages, en ms (défaut 1500). */
  intervalMs?: number;
  /** Sleep injectable (défaut `setTimeout`). */
  sleep?: (ms: number) => Promise<void>;
  /** Interruption coopérative (démontage du composant). */
  isCancelled?: () => boolean;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Sonde `checkAllowed` jusqu'à ce qu'il renvoie `true` ou que le nombre max de
 * tentatives soit atteint. Renvoie `true` si l'accès a été débloqué, `false`
 * en cas de timeout ou d'annulation. Ne jette jamais : une erreur de
 * `checkAllowed` est traitée comme « pas encore débloqué » et on retente.
 */
export async function pollExerciseUnlock(opts: UnlockPollOptions): Promise<boolean> {
  const {
    checkAllowed,
    maxAttempts = 20,
    intervalMs = 1500,
    sleep = defaultSleep,
    isCancelled,
  } = opts;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (isCancelled?.()) return false;
    let allowed = false;
    try {
      allowed = await checkAllowed();
    } catch {
      allowed = false;
    }
    if (allowed) return true;
    if (attempt < maxAttempts - 1) await sleep(intervalMs);
  }
  return false;
}
